// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import { observable, action, makeObservable } from 'mobx';
import { ROLES } from '@shared/constants/user';

export interface SystemUserDTO {
  sub: string; // unique identifier
  role: string;
  permission: string;
  img: string;
  fullName: string;
  isHostingCompany: boolean;
}

export interface SystemUserProperties extends Omit<SystemUserDTO, 'role' | 'permission' | 'sub'> {
  roles: Array<string>;
  permissions: Array<string>;
  id: number;
}

export default class SystemUser {
  id: number;
  roles: Array<string>;
  permissions: Array<string>;
  isHostingCompany: boolean;
  @observable img: string;
  @observable fullName: string;

  constructor(dto: SystemUserDTO) {
    this.update(dto);
    makeObservable(this);
  }

  get isProjectManager() {
    return this.roles.includes(ROLES.projectManager);
  }

  get asJson(): SystemUserProperties {
    return {
      id: this.id,
      roles: this.roles,
      permissions: this.permissions,
      img: this.img,
      fullName: this.fullName,
      isHostingCompany: this.isHostingCompany
    };
  }

  @action update(newData: Partial<SystemUserDTO>): SystemUserProperties {
    const { role, permission, sub, ...otherNewData } = newData;
    const allData = { ...this.asJson, ...otherNewData };

    Object.assign(this, allData);

    if (sub) {
      this.id = Number(sub);
    }

    if (role) {
      this.roles = JSON.parse(role);
    }

    if (permission) {
      this.permissions = JSON.parse(permission);
    }

    return this.asJson;
  }
}